import type { StoreData } from "@components/store-locator/controller";
import { fetchStores } from "@components/store-locator/fetch-store";
import { normalizeStore } from "@components/store-locator/normalize-store";
import { QueryKeys } from "@lib/enums/QueryKeys";
import type { QueryConfig } from "@lib/helpers/useApi";
import { useApi } from "@lib/helpers/useApi";
import { useSession } from "next-auth/react";

type UseStoresParams<TData = StoreData[]> = {
    country?: string;
    locale: string;
    isStoreLocatorPage?: boolean;
    queryConfig?: QueryConfig<StoreData[], TData>;
};

export const useStores = ({
    country,
    isStoreLocatorPage = false,
    locale,
    queryConfig = {},
}: UseStoresParams) => {
    const { data: session, status } = useSession();
    const queryKey = [
        QueryKeys.getStores,
        country?.toUpperCase(),
        locale?.toUpperCase(),
        isStoreLocatorPage,
    ];

    const enabledByQueryConfig = queryConfig.enabled ?? true;

    const { data, ...rest } = useApi.useAsync(
        queryKey,
        async () => {
            const stores = await fetchStores(country, isStoreLocatorPage, session?.accessToken);
            return normalizeStore(stores, locale);
        },
        {
            ...queryConfig,
            enabled: enabledByQueryConfig && !!session?.accessToken && status === "authenticated",
        }
    );

    return { data, ...rest };
};
